<template>
  <div class="panel" v-if="cmSectionState !== 'empty'">
    <div class="cm-sec-head">
      <h2>{{ $t('CM_TRENDS') }}</h2>
      <p class="cm-hint">{{ $t('CM_TRENDS_HINT') }}</p>
    </div>

    <div v-if="loading" class="cm-state">{{ $t('CM_LOADING') }}</div>
    <div v-else-if="error" class="cm-state is-error">{{ error }}</div>

    <template v-else-if="data">
      <div v-if="data.low_data" class="cm-lowdata">{{ $t('CM_TRENDS_LOWDATA') }}</div>

      <div  :class="risingSorted.length && newSorted.length ? 'cm-grid-2' : null">
        <div class="cm-trends-section" v-if="risingSorted.length">
          <div class="cm-chart-title">{{ $t('CM_TRENDS_RISING') }}</div>
          <table class="cm-table">
            <thead>
              <tr>
                <th
                  :class="classTableHead('rising', 'term')"
                  @click="toggleSort('rising', 'term')"
                >{{ $t('CM_COL_TERM') }}</th>
                <th
                  :class="classTableHead('rising', 'growth')"
                  @click="toggleSort('rising', 'growth')"
                >{{ $t('CM_COL_GROWTH') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in visibleRising"
                :key="row.term"
                :class="row.added ? 'is-tracked' : null"
              >
                <td>
                  <span class="cm-term" @click="openTerm(row.term)">
                    <template v-if="row.added">★ </template>{{ row.term }}
                  </span>
                </td>
                <td>
                  <span class="cm-bar">
                    <span class="cm-bar-track">
                      <span
                        class="cm-bar-fill cm-tone-green"
                        :style="{ width: growthWidth(row.growth_pct) }"
                      ></span>
                    </span>
                    <span class="cm-bar-val cm-rel is-up">+{{ row.growth_pct }}%</span>
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
          <button v-if="risingSorted.length > limit" class="cm-more" @click="showAllRising = !showAllRising">
            {{ showAllRising ? $t('CM_SHOW_LESS') : $t('CM_SHOW_ALL', { n: risingSorted.length }) }}
          </button>
        </div>

        <div class="cm-trends-section" v-if="newSorted.length">
          <div class="cm-chart-title">{{ $t('CM_TRENDS_NEW') }}</div>
          <table class="cm-table">
            <thead>
              <tr>
                <th
                  :class="classTableHead('neu', 'term')"
                  @click="toggleSort('neu', 'term')"
                >{{ $t('CM_COL_TERM') }}</th>
                <th
                  :class="['is-num', classTableHead('neu', 'posts')]"
                  @click="toggleSort('neu', 'posts')"
                >{{ $t('CM_COL_POSTS') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in visibleNew"
                :key="row.term"
                :class="row.added ? 'is-tracked' : null"
              >
                <td>
                  <span class="cm-term" @click="openTerm(row.term)">
                    <template v-if="row.added">★ </template>{{ row.term }}
                  </span>
                </td>
                <td class="is-num">{{ row.posts_count }}</td>
              </tr>
            </tbody>
          </table>
          <button v-if="newSorted.length > limit" class="cm-more" @click="showAllNew = !showAllNew">
            {{ showAllNew ? $t('CM_SHOW_LESS') : $t('CM_SHOW_ALL', { n: newSorted.length }) }}
          </button>
        </div>
      </div>

      <!-- рядов нет, но посты в окне есть: объясняем порог, а не прячем раздел -->
      <div v-if="!risingSorted.length && !newSorted.length" class="cm-state">
        {{ $t('CM_TRENDS_EMPTY', { posts: minRecentPosts, days: recentDays }) }}
      </div>
    </template>
  </div>
</template>

<script>
import cmScopeMixin from "@/mixins/cmScopeMixin";
import cmSectionMixin from "@/mixins/cmSectionMixin";
import cmTableSortMixin, { createSortState } from "@/mixins/cmTableSortMixin";
import cmApi from "@/api/controllers/content-monitoring";
import bus from "@/models/content-monitoring/bus";

export default {
  name: "Cm-trends",
  mixins: [cmScopeMixin, cmSectionMixin, cmTableSortMixin],

  data() {
    return {
      data: null,
      loading: false,
      error: "",
      limit: 10,
      showAllRising: false,
      showAllNew: false,
      sorts: {
        rising: createSortState(["term", "growth"], "term"),
        neu: createSortState(["term", "posts"], "term"),
      },
    };
  },

  computed: {
    /**
     * Есть ли что показывать. Кроме самих рядов — ещё и объяснение пустоты:
     * пока в свежем окне есть посты, раздел остаётся на месте и говорит, какого
     * порога не хватило. Если постов в окне нет вовсе — прячемся, как остальные
     * секции (за общий текст «данных нет» отвечает вкладка).
     */
    hasData() {
      return this.risingRows.length > 0 || this.newRows.length > 0 || this.recentPostsTotal > 0;
    },

    recentPostsTotal() {
      return (this.data && this.data.recent_posts_total) || 0;
    },

    recentDays() {
      return (this.data && this.data.recent_days) || 0;
    },

    minRecentPosts() {
      return (this.data && this.data.min_recent_posts) || 0;
    },

    risingRows() {
      return (this.data && this.data.rising) || [];
    },

    newRows() {
      return (this.data && this.data.new) || [];
    },

    risingSorted() {
      return this.sortRows(this.risingRows, "rising", {
        fieldMap: { term: "term", growth: "growth_pct" },
        stringFields: ["term"],
      });
    },

    newSorted() {
      return this.sortRows(this.newRows, "neu", {
        fieldMap: { term: "term", posts: "posts_count" },
        stringFields: ["term"],
      });
    },

    visibleRising() {
      return this.showAllRising ? this.risingSorted : this.risingSorted.slice(0, this.limit);
    },

    visibleNew() {
      return this.showAllNew ? this.newSorted : this.newSorted.slice(0, this.limit);
    },

    maxGrowth() {
      if (!this.risingSorted.length) return 1;
      return Math.max(...this.risingSorted.map((r) => r.growth_pct || 0), 1);
    },
  },

  methods: {
    growthWidth(value) {
      return `${Math.max(2, Math.round((value / this.maxGrowth) * 100))}%`;
    },
    openTerm(term) {
      bus.$emit("cm:term", term);
    },

    load() {
      return this.cmLoad(
        () => cmApi.fetchTrends(this.cmScope, this.scope),
        (data) => {
          this.data = {
            ...data,
            rising: (data && data.rising) || [],
            new: (data && data.new) || [],
          };
        },
        { silent: true },
      );
    },
  },
};
</script>
